'use client';

import type { IconName } from 'lucide-react/dynamic';
import { DynamicIcon, dynamicIconImports } from 'lucide-react/dynamic';
import * as React from 'react';

export type SocialPlatform =
  | 'instagram'
  | 'linkedin'
  | 'twitter'
  | 'x'
  | 'facebook'
  | 'tiktok'
  | 'youtube'
  | 'github'
  | 'behance'
  | 'dribbble'
  | 'pinterest'
  | 'snapchat'
  | 'twitch'
  | 'custom';

export type SocialIconProps = {
  className?: string;
};

export type SocialIconComponent = React.ComponentType<SocialIconProps>;

/** Inline Simple Icons paths — no Iconify CDN (works offline / Capacitor). */
function brandSvg(path: string, displayName: string): SocialIconComponent {
  const IconComponent = ({ className }: SocialIconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d={path} />
    </svg>
  );
  IconComponent.displayName = displayName;
  return IconComponent;
}

function safeLucideIconName(raw: string): IconName {
  const t = raw.trim();
  if (t && t in dynamicIconImports) return t as IconName;
  return 'link';
}

/**
 * Brand glyphs inlined from Simple Icons. Custom links may store a Lucide icon
 * name in `icon` and render via DynamicIcon.
 */
export const SOCIAL_ICONS: Record<SocialPlatform, SocialIconComponent> = {
  instagram: brandSvg(
    'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
    'SocialInstagram',
  ),
  linkedin: brandSvg(
    'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z',
    'SocialLinkedIn',
  ),
  twitter: brandSvg(
    'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    'SocialX',
  ),
  x: brandSvg(
    'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
    'SocialX',
  ),
  facebook: brandSvg(
    'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
    'SocialFacebook',
  ),
  tiktok: brandSvg(
    'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.95-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
    'SocialTikTok',
  ),
  youtube: brandSvg(
    'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
    'SocialYouTube',
  ),
  github: brandSvg(
    'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12',
    'SocialGitHub',
  ),
  behance: brandSvg(
    'M22 7h-7v-2h7v2zm1.726 10c-.442 1.297-2.029 3-5.101 3-3.074 0-5.564-1.729-5.564-5.675 0-3.91 2.325-5.92 5.466-5.92 3.082 0 4.964 1.782 5.375 4.426.078.506.109 1.188.095 2.14H15.97c.13 3.211 3.483 3.312 4.588 2.029h3.168zm-7.686-4h4.565c-.075-1.619-1.198-2.995-2.34-2.995-1.318 0-2.1 1.079-2.225 2.995zM0 6h5.5c1.8 0 3.3.7 3.3 2.6 0 1.2-.7 2.1-1.7 2.4 1.4.3 2.3 1.5 2.3 3 0 2.3-1.8 3.5-4.2 3.5H0V6zm4.7 5.5c1.1 0 1.7-.6 1.7-1.5 0-1-.6-1.4-1.7-1.4H2.5v2.9h2.2zm.2 5c1.3 0 2-.6 2-1.7 0-1.1-.7-1.7-2-1.7H2.5V16.5h2.4z',
    'SocialBehance',
  ),
  dribbble: brandSvg(
    'M12 24C5.385 24 0 18.615 0 12S5.385 0 12 0s12 5.385 12 12-5.385 12-12 12zm10.12-10.358c-.35-.11-3.17-.953-6.384-.438 1.34 3.684 1.887 6.684 1.992 7.308 2.3-1.555 3.936-4.02 4.395-6.87zm-6.115 7.808c-.153-.9-.75-4.032-2.205-7.788l-.068.02c-5.79 2.015-7.86 6.03-8.023 6.358 1.737 1.358 3.923 2.166 6.29 2.166 1.42 0 2.772-.29 4.006-.756zm-11.62-2.58c.232-.4 3.045-5.055 8.332-6.765.135-.045.27-.084.405-.12-.26-.585-.54-1.167-.832-1.74C7.17 11.775 2.23 11.71 1.785 11.7l-.016.472c0 2.56.995 4.902 2.616 6.641zm-1.16-9.185c.46.008 4.69.065 9.477-1.246-1.698-3.018-3.53-5.558-3.8-5.928-2.868 1.35-5.01 3.99-5.676 7.17zM9.62 2.85c.282.38 2.156 2.912 3.822 6 3.645-1.365 5.19-3.44 5.373-3.72C17.08 3.41 14.66 2.35 12 2.35c-.825 0-1.622.17-2.38.5zm10.4 4.015c-.216.29-1.938 2.493-5.728 4.04.24.49.47.985.68 1.486.08.193.15.384.22.579 3.41-.43 6.8.26 7.14.33-.02-2.42-.88-4.64-2.31-6.435z',
    'SocialDribbble',
  ),
  pinterest: brandSvg(
    'M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.012-4.869-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z',
    'SocialPinterest',
  ),
  snapchat: brandSvg(
    'M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.264.299 4.441 1.538.433 2.908 1.407 3.605 2.422 1.09 1.592.98 2.771.477 3.521-.632.94-1.943 1.28-2.893 1.038.208.678.485 1.321.838 1.916.353.595.418 1.142.187 1.603-.23.46-.74.67-1.393.63-.654-.04-1.446-.31-2.22-.7-.387 1.17-1.2 2.25-2.39 3.05-.91.61-1.95.94-3.04.94s-2.13-.33-3.04-.94c-1.19-.8-2.003-1.88-2.39-3.05-.774.39-1.566.66-2.22.7-.653.04-1.163-.17-1.393-.63-.231-.461-.166-1.008.187-1.603.353-.595.63-1.238.838-1.916-.95.242-2.261-.098-2.893-1.038-.503-.75-.613-1.929.477-3.521.697-1.015 2.067-1.989 3.605-2.422-.104-1.177-.23-3.248.299-4.441C7.859 1.069 11.216.793 12.206.793z',
    'SocialSnapchat',
  ),
  twitch: brandSvg(
    'M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z',
    'SocialTwitch',
  ),
  custom: brandSvg(
    'M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z',
    'SocialLink',
  ),
};

export function getSocialIcon(platform: SocialPlatform): SocialIconComponent {
  return SOCIAL_ICONS[platform] ?? SOCIAL_ICONS.custom;
}

const SOCIAL_PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  twitter: 'X (Twitter)',
  x: 'X (Twitter)',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  github: 'GitHub',
  behance: 'Behance',
  dribbble: 'Dribbble',
  pinterest: 'Pinterest',
  snapchat: 'Snapchat',
  twitch: 'Twitch',
  custom: 'Custom',
};

/** Human-readable platform name when no custom label is set. */
export function getSocialPlatformLabel(platform: SocialPlatform): string {
  return SOCIAL_PLATFORM_LABELS[platform] ?? SOCIAL_PLATFORM_LABELS.custom;
}

/** Renders the correct icon for a social link (inline brands, Lucide for custom with `icon`). */
export function SocialLinkIcon({
  link,
  className,
}: {
  link: { platform: SocialPlatform; icon?: string };
  className?: string;
}) {
  if (link.platform === 'custom' && link.icon?.trim()) {
    return (
      <DynamicIcon
        name={safeLucideIconName(link.icon)}
        className={className}
        fallback={() => (
          <span aria-hidden className={['inline-block', className].join(' ')} />
        )}
      />
    );
  }
  const BrandIcon =
    (SOCIAL_ICONS as Record<string, SocialIconComponent>)[link.platform] ??
    SOCIAL_ICONS.custom;
  return <BrandIcon className={className} />;
}
